// src/data/worksheets/resilience.js — performance-side pair (authored).
// Built off the Resilience action plan: name it, one next move, recovery counts.

export const RESILIENCE_CHECKLIST = {
    id: 'resilience_checklist', topicId: 'resilience', kind: 'checklist',
    title: 'Resilience Checklist',
    intro: 'Bouncing back is a set of habits, not a personality trait. Tick these off as you build them in.',
    sections: [
        { id: 'setback', title: 'In the Moment of a Setback', prompts: [
            { id: 'name', type: 'checklist', label: 'Name It', items: [
                { id: 'n1', label: 'Say what happened in one plain sentence — no story, no verdict.', detail: 'Naming it takes some of the heat out.' },
                { id: 'n2', label: 'Notice the feeling and let it be there for a minute before you act.' }
            ] },
            { id: 'next', type: 'checklist', label: 'One Next Move', items: [
                { id: 'x1', label: 'Pick the single next action, not the whole fix.' },
                { id: 'x2', label: 'Do it within the hour, however small.' }
            ] }
        ] },
        { id: 'recovery', title: 'Recovery Counts', prompts: [
            { id: 'basics', type: 'checklist', label: 'The Basics Are the Bounce', items: [
                { id: 'b1', label: 'Sleep, food and movement are part of bouncing back, not a reward for it.' },
                { id: 'b2', label: 'Protect one recovery habit this week even when things are busy.' }
            ] },
            { id: 'people', type: 'checklist', label: 'Use Your People', items: [
                { id: 'p1', label: 'Tell one person what happened within a day.' },
                { id: 'p2', label: 'Ask for a specific kind of help (a listen, advice, a hand) — people help better when they know which.' }
            ] }
        ] },
        { id: 'thinking', title: 'Thinking That Helps', prompts: [
            { id: 'reframe', type: 'checklist', label: 'Separate the Event from the Verdict', items: [
                { id: 'r1', label: 'Catch "this always happens" and "I\'m useless" — replace with what is actually true right now.' },
                { id: 'r2', label: 'Ask: what would I say to a {teammate|colleague} in this spot? Say that to yourself.' }
            ] },
            { id: 'control', type: 'checklist', label: 'Control the Controllables', items: [
                { id: 'c1', label: 'List what you can influence about the situation and what you cannot.' },
                { id: 'c2', label: 'Put your effort on the first list only.' }
            ] }
        ] },
        { id: 'building', title: 'Building Capacity Over Time', prompts: [
            { id: 'practice', type: 'checklist', label: 'Practice Small Discomfort', items: [
                { id: 'd1', label: 'Do one slightly uncomfortable thing a week on purpose (a hard conversation, a cold start, a new skill).' },
                { id: 'd2', label: 'Finish things when they stop being fun — that is where the strength is built.' }
            ] },
            { id: 'meaning', type: 'checklist', label: 'Keep the Why in View', items: [
                { id: 'm1', label: 'Write one line on why this matters to you and keep it where you\'ll see it.' },
                { id: 'm2', label: 'Reconnect with it after a bad day, not just a good one.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'w1', label: 'Note one setback from the week and how long it took to get moving again.' },
                { id: 'w2', label: 'Name one thing that helped you bounce — do it on purpose next time.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'Resilience is measured in recovery time, not in never falling. Faster is the goal — not perfect.',
    supportNote: 'If a setback has knocked you further than usual and you are not coming back up, talking to someone is the strong move.'
};

export const RESILIENCE_WORKSHEET = {
    id: 'resilience_worksheet', topicId: 'resilience', kind: 'worksheet',
    title: 'Resilience Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'bounce', type: 'scale', label: 'How quickly do you bounce back from a setback right now?', help: 'Think about the last few weeks.', min: 1, max: 10, minLabel: 'Days or more', maxLabel: 'Within the hour' },
            { id: 'setbacks', type: 'list', label: 'Recent setbacks', help: 'Three things that knocked you recently — big or small.', count: 3 },
            { id: 'reactions', type: 'list', label: 'How you tend to react', help: 'e.g., go quiet, get angry, overtrain, avoid people, replay it at night.', count: 3 },
            { id: 'cost', type: 'text', label: 'What does a slow bounce-back cost you?', multiline: true }
        ] },
        { id: 'understanding', title: 'Understanding Your Bounce', prompts: [
            { id: 'best', type: 'text', label: 'A time you bounced back well — what did you do?', help: 'This is your template. Be specific about the first thing you did.', multiline: true },
            { id: 'verdicts', type: 'list', label: 'The verdicts you hand yourself', help: 'e.g., "I always choke", "I\'m not cut out for this". Write them down so you can see them coming.', count: 3 },
            { id: 'truths', type: 'list', label: 'What is actually true instead', help: 'One factual replacement for each verdict.', count: 3 }
        ] },
        { id: 'building', title: 'Building Resilience', prompts: [
            { id: 'nextmove', type: 'text', label: 'Your "one next move" rule', help: 'What is the first small action you will take after a setback? Write it as an instruction to yourself.', multiline: true },
            { id: 'recoveryhabit', type: 'text', label: 'The recovery habit you will protect this week', help: 'Sleep, food, movement, downtime — pick one.' },
            { id: 'people', type: 'list', label: 'Your people', help: 'Who you will tell within a day, and what kind of help you\'ll ask for.', count: 2 },
            { id: 'controllables', type: 'list', label: 'Controllables in your current challenge', help: 'Things you can actually influence — effort goes here.', count: 3 }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'discomfort', type: 'text', label: 'One uncomfortable thing you will do on purpose this week' },
            { id: 'why', type: 'text', label: 'Why this matters to you', help: 'One line. Keep it where you\'ll see it.' },
            { id: 'goal', type: 'text', label: 'This week\'s resilience goal', help: 'e.g., "get moving within an hour of any setback".' }
        ] },
        { id: 'track', title: 'Track It', description: 'Log the setbacks, not just the good days — recovery time is the number that matters.', prompts: [
            { id: 'tracker', type: 'table', label: 'Bounce-back tracker', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'setback', label: 'Setback', kind: 'text' },
                { id: 'bounce', label: 'Bounce-back (1–10, 10 = fast)', kind: 'number', min: 1, max: 10 },
                { id: 'helped', label: 'What helped', kind: 'text' },
                { id: 'notes', label: 'Notes', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'bounce', label: 'Bounce-back' }] } }
        ] }
    ],
    outro: 'Falling is not the problem. Getting up a little faster each time is the whole game.',
    supportNote: 'If a setback has knocked you further than usual and you are not coming back up, talking to someone is the strong move.'
};

export const RESILIENCE_WORKSHEETS = [RESILIENCE_CHECKLIST, RESILIENCE_WORKSHEET];
